import type {CSSProperties} from 'react';
import {radius, weight} from '../scale.js';
import {wedgeExactRole} from './wedgeRoles.js';

/* The starter buttons come in four rails (the stripe on the left edge turns amber, accent, blue, purple down the list; the fade-in is staggered 60ms
   per place) and two motions (a fade, none). Every other look is one button of the thread body. */
export type WedgeBlipBodyLook =
  | 'blip-body-end' | 'blip-body-top' | 'blip-body-ctx'
  | 'blip-body-start-0' | 'blip-body-start-1' | 'blip-body-start-2' | 'blip-body-start-3'
  | 'blip-body-start-0-still' | 'blip-body-start-1-still' | 'blip-body-start-2-still' | 'blip-body-start-3-still'
  | 'blip-body-follow' | 'blip-body-pick' | 'blip-body-pick-warn' | 'blip-body-cancel' | 'blip-body-limit' | 'blip-body-retry';

const BLIP_BODY_LOOKS = new Set<string>([
  'blip-body-end', 'blip-body-top', 'blip-body-ctx',
  'blip-body-start-0', 'blip-body-start-1', 'blip-body-start-2', 'blip-body-start-3',
  'blip-body-start-0-still', 'blip-body-start-1-still', 'blip-body-start-2-still', 'blip-body-start-3-still',
  'blip-body-follow', 'blip-body-pick', 'blip-body-pick-warn', 'blip-body-cancel', 'blip-body-limit', 'blip-body-retry',
]);
export const isWedgeBlipBodyLook = (look: string): look is WedgeBlipBodyLook => BLIP_BODY_LOOKS.has(look);

const RAILS = ['amber', 'accent', 'blue', 'purple'] as const;
const STAGGER_MS = 60;

/* Released Blip thread body (Wedge): the buttons between the sheet head and the input bar. Each reads the exact legacy roles, and the declaration
   order is the order of the source. No look draws a transparent target (the `blip-` hit rule removes it): each control sits in a row or a stack at its
   released box.
   End is the small button in the rehearsal banner (a 0.5px separator border, a transparent fill, the secondary ink, 11px bold, 30px tall at least).
   Top is the amber card that lists the accounts to PREP (the whole card is the button, 13px corners). Ctx is the amber starter that asks for the play
   on the open brief. Start is a card with a 1px border and a 3px rail on its left edge; it fades in with a stagger. Follow is the secondary pill under
   an answer. Pick is a full-width row of the intel confirm card (a 0.5px separator border); the warn look takes the 1px red border for a distributor
   or a closed place. Cancel is the quiet text button below the rows. Limit and retry are the plain text buttons of the error line (amber and red). */
function start(rail: number, still: boolean): CSSProperties {
  const r = wedgeExactRole;
  return {
    textAlign: 'left', padding: '11px 13px 11px 12px', borderRadius: 13, border: `1px solid ${r('cardBorder')}`, borderLeft: `3px solid ${r(RAILS[rail])}`,
    background: r('surface'), color: r('label'), fontSize: 13.5, fontWeight: weight.medium, cursor: 'pointer',
    animation: still ? 'none' : `prepFadeUp 320ms var(--ease) ${rail * STAGGER_MS}ms both`,
    transition: still ? 'none' : 'transform 140ms var(--ease)',
  };
}

function pick(warn: boolean): CSSProperties {
  const r = wedgeExactRole;
  return {
    width: '100%', textAlign: 'left', display: 'block', marginBottom: 7, padding: '9px 11px', borderRadius: 11, cursor: 'pointer',
    font: 'inherit', fontFamily: r('font'), background: r('bgElev'), border: warn ? `1px solid ${r('red')}` : `0.5px solid ${r('separator')}`,
  };
}

export function wedgeBlipBodyStyle(look: WedgeBlipBodyLook): CSSProperties {
  const r = wedgeExactRole;
  if (look === 'blip-body-end') return {
    background: 'transparent', border: `0.5px solid ${r('separator')}`, borderRadius: radius.sm, padding: '5px 9px', color: r('labelSec'), fontSize: 11,
    fontWeight: weight.bold, cursor: 'pointer', minHeight: 30, WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation',
  };
  if (look === 'blip-body-top') return {
    display: 'block', width: '100%', textAlign: 'left', font: 'inherit', color: 'inherit', border: `0.5px solid ${r('amber')}`, borderRadius: 13,
    padding: '11px 12px', marginBottom: 12, cursor: 'pointer', background: r('amberSoft'),
  };
  if (look === 'blip-body-ctx') return {
    textAlign: 'left', padding: '11px 13px', borderRadius: 13, border: `0.5px solid ${r('amber')}`, background: r('amberSoft'), color: r('amber'),
    fontSize: 13.5, fontWeight: weight.bold, cursor: 'pointer',
  };
  if (look === 'blip-body-follow') return {
    padding: '7px 12px', borderRadius: radius.pill, border: `1px solid ${r('cardBorder')}`, background: r('surface'), color: r('labelSec'), fontSize: 12.5,
    fontWeight: weight.semibold, cursor: 'pointer',
  };
  if (look === 'blip-body-pick') return pick(false);
  if (look === 'blip-body-pick-warn') return pick(true);
  if (look === 'blip-body-cancel') return {
    font: 'inherit', fontFamily: r('font'), fontSize: 12, fontWeight: weight.semibold, color: r('labelTer'), background: 'transparent', border: 'none',
    cursor: 'pointer', padding: '2px 0',
  };
  if (look === 'blip-body-limit' || look === 'blip-body-retry') return {
    border: 'none', background: 'transparent', color: look === 'blip-body-limit' ? r('amber') : r('red'), fontWeight: weight.bold, cursor: 'pointer',
  };
  return start(Number(look.charAt('blip-body-start-'.length)), look.endsWith('-still'));
}
